import { useEffect, useMemo } from 'react'
import { useBlogCms } from '@/hooks/useBlogCms'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import { BLOG_CONTENT_API, SITE_URL } from '@/constants'
import { replaceString } from '@/utils/replaceString'
import type { NextPage, GetServerSidePropsContext } from 'next'
import {
  Box,
  Center,
  Flex,
  Progress,
  Text,
  useColorModeValue,
  useStyleConfig,
} from '@chakra-ui/react'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { Pagination } from '@/components/Pagination'
import { Article } from '@/features/education/article'

const AuthorPage: NextPage = () => {
  const styles = useStyleConfig('Box', { variant: 'white' })
  const t = useTranslations()
  const { compassContent, loading, getContent } = useBlogCms()
  const {
    compassContent: tags,
    loading: loadingTag,
    getContent: getTag,
  } = useBlogCms()
  const router = useRouter()

  useEffect(() => {
    if (!router?.query?.slug) return

    const abortController = new AbortController()
    getContent(
      {
        path: `/content/posts`,
        params: `&filter=tag:${router?.query?.slug}&page=1&limit=24&include=tags,authors&order=published_at%20desc`,
      },
      abortController
    )

    const abortController2 = new AbortController()
    getTag(
      {
        path: `/content/tags/slug/${router?.query?.slug}`,
        params: '',
      },
      abortController2
    )

    return () => {
      abortController.abort()
      abortController2.abort()
    }
  }, [router])

  const pagingHandler = (paging: any) => {
    const abortController = new AbortController()

    const limit = compassContent?.meta?.pagination?.limit
    let params = ''
    if (paging?.firstPage) {
      params = `&page=1&limit=24`
    } else if (paging?.lastPage) {
      params = `&page=${compassContent?.meta?.pagination?.pages}&limit=${limit}`
    } else if (paging?.next) {
      params = `&page=${compassContent?.meta?.pagination?.next}&limit=${limit}`
    } else if (paging?.prev) {
      params = `&page=${compassContent?.meta?.pagination?.prev}&limit=${limit}`
    } else if (paging?.changeLimit) {
      params = `&page=1&limit=${paging?.pageLimit}`
    } else {
      params = `&page=${compassContent?.meta?.pagination?.next}&limit=${limit}`
    }

    return getContent(
      {
        path: '/content/posts',
        params: `${params}&filter=tag:${router?.query?.slug}&include=tags,authors&order=published_at%20desc`,
      },
      abortController
    )
  }

  const tag = tags?.tags?.length > 0 ? tags?.tags[0] : null
  const tagTextColor = useColorModeValue('black', 'white')

  return (
    <>
      {(loading || loadingTag) && (
        <Progress size="xs" isIndeterminate colorScheme="brand" />
      )}
      <Box __css={styles}>
        <Box
          width="100%"
          minHeight={{ base: '165px', md: '282px' }}
          backgroundImage={`url(${tag?.feature_image})`}
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          backgroundPosition="center"
        >
          <Box background="rgba(0,0,0,.18)">
            <ContainerBase
              height="100%"
              maxWidth={{ base: '100%', xl: '1200px' }}
            >
              {tag && (
                <Flex
                  width="100%"
                  minHeight={{ base: '165px', md: '282px' }}
                  flexDirection="column"
                  justifyContent={{ base: 'center', md: 'flex-end' }}
                  alignItems={{ base: 'center', md: 'unset' }}
                  pt="5px"
                  pb="20px"
                  color={tag?.feature_image ? 'white' : tagTextColor}
                  textShadow={tag?.feature_image ? '0 1px 1px black' : 'none'}
                >
                  <Text
                    fontSize={{ base: '32px', md: '50px' }}
                    fontWeight="600"
                  >
                    {tag?.name}
                  </Text>
                  <Text fontSize={{ base: '18px', md: '21px' }} opacity="0.8">
                    {tag?.description ||
                      `A collection of ${compassContent?.meta?.pagination?.total} posts`}
                  </Text>
                </Flex>
              )}
            </ContainerBase>
          </Box>
        </Box>
        <ContainerBase py="50px" maxWidth={{ base: '100%', xl: '1200px' }}>
          <Flex
            flexWrap="wrap"
            paddingTop="40px"
            justifyContent="space-between"
          >
            {compassContent?.posts?.map((article: any) => {
              return <Article key={article?.id} article={article} />
            })}
          </Flex>
          <Center>
            {useMemo(
              () =>
                compassContent?.meta?.pagination && (
                  <Pagination
                    perPageLabel={`Articles per page`}
                    pageOptions={['6', '9', '24', '60']}
                    pageLimit={compassContent?.meta?.pagination?.limit}
                    refetch={null}
                    pagingHandler={pagingHandler}
                    totalCount={compassContent?.meta?.pagination?.total}
                    pageInfo={{
                      endCursor: '',
                      hasNextPage: !!compassContent?.meta?.pagination?.next,
                      hasPreviousPage: !!compassContent?.meta?.pagination?.prev,
                      startCursor: '',
                      __typename: '',
                    }}
                  />
                ),
              [compassContent?.meta?.pagination]
            )}
          </Center>
        </ContainerBase>
      </Box>
    </>
  )
}

export async function getServerSideProps({
  locale,
  params,
}: GetServerSidePropsContext) {
  let tag: any = {}
  try {
    const resource = (path: string) => {
      return replaceString(BLOG_CONTENT_API, {
        key: 'resource',
        value: path,
      })
    }

    const path = `/content/tags/slug/${params?.slug}`
    const res = await fetch(`${resource(path)}`, {})
    const tags = await res.json()
    tag = tags?.tags?.length > 0 ? tags?.tags[0] : null

    if (!res.ok) {
      if (res.status === 404) {
        return { notFound: true }
      }
    }
  } catch (e) {
    console.error(JSON.stringify(e))
  }

  return {
    props: {
      messages: (await import(`../../../../locales/${locale}.json`)).default,
      revalidate: 60,
      htmlClass: 'education',
      seo: {
        title: tag?.meta_title || '',
        description: tag?.meta_description || '',
        image: tag?.feature_image || tag?.og_image || '',
        url: `${SITE_URL}/education/tag/${params?.slug}`,
        breadcrumbs: [
          {
            position: 2,
            item: `${SITE_URL}/education`,
            name: 'Compass Mining Content',
          },
          {
            position: 3,
            item: `${SITE_URL}/education/tag/${params?.slug}`,
            name: tag?.meta_title || '',
          },
        ],
      },
    },
  }
}

export default AuthorPage
